import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaUser, FaInfoCircle, FaFileAlt, FaWallet } from "react-icons/fa";
import profile from "../Images/profile.jpg";
import countryList from "react-select-country-list";
import { getUserdata, sendProfiledata } from "../Redux/Action";
import { useAppTheme } from "../Hooks/useAppTheme";

const ProfileCard = ({ titleFirst, titleSecond, icon, children }) => {
  const theme = useAppTheme();
  
  return (
    <div className="relative rounded-xl shadow px-4 pt-4 pb-4 w-full overflow-visible" style={{ backgroundColor: theme.bgCard }}>
      <div className="absolute top-0 left-4 -translate-y-1/2 p-5 rounded-2xl shadow-xl text-2xl" style={{ background: theme.iconBg, color: theme.iconText }}>
        {icon}
      </div>
      <h3 className="text-right text-lg font-semibold leading-snug mb-4">
        <span className="block" style={{ color: theme.textSecondary }}>{titleFirst}</span>
        <span className="block" style={{ color: theme.brandAccent }}>{titleSecond}</span>
      </h3>
      {children}
    </div>
  );
};


const Field = ({ label, value, onChange, suggestions, onSelectSuggestion, options }) => {
  const wrapperRef = useRef(null);
  const theme = useAppTheme();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (onSelectSuggestion) onSelectSuggestion(null, true); // signal to hide suggestions
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onSelectSuggestion]);

  return (
    <div className="text-sm relative" ref={wrapperRef}>
      <div className="font-medium" style={{ color: theme.textSecondary }}>{label}</div>

      {options ? (
        <select
          className="mt-1 w-full rounded px-3 py-2 border transition-colors duration-200 focus:outline-none focus:ring-2"
          value={value ? "true" : "false"} // convert boolean to string
          onChange={(e) => onChange({ target: { value: e.target.value === "true" } })} // convert string back to boolean
          style={{ 
            backgroundColor: theme.inputBg, 
            color: theme.inputText,
            borderColor: theme.inputBorder,
            boxShadow: `0 0 0 0px ${theme.brandPrimary}`
          }}
        >
          {options.map((opt, idx) => (
            <option key={idx} value={opt.value.toString()}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          className="mt-1 w-full rounded px-3 py-2 border transition-colors duration-200 focus:outline-none focus:ring-2"
          value={value || ""}
          onChange={onChange}
          style={{ 
            backgroundColor: theme.inputBg, 
            color: theme.inputText,
            borderColor: theme.inputBorder,
            boxShadow: `0 0 0 0px ${theme.brandPrimary}`
          }}
        />
      )}

      {suggestions && suggestions.length > 0 && (
        <div className="absolute z-10 shadow-lg rounded w-full max-h-40 overflow-y-auto border" style={{ backgroundColor: theme.bgCard, borderColor: theme.borderPrimary }}>
          {suggestions.map((s, idx) => (
            <div
              key={idx}
              className="px-3 py-2 cursor-pointer transition-colors duration-150"
              onClick={() => onSelectSuggestion(s)}
              style={{ color: theme.textPrimary }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const UserProfile = ({ activeTab }) => {
  const userData = useSelector((state) => state?.Login?.userData);
  const theme = useAppTheme();

  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    dob: "",
    nationality: "",
    preferredLanguage: "",
    educationLevel: "",
    employed: true,
    employmentType: "",
    residenceCountry: "",
    region: "",
    city: "",
    streetName: "",
    grossIncome: "",
    netWorth: "",
    sourceOfFunding: "",
    tradingObjectives: "",
  });

  const countries = countryList().getData().map((c) => c.label);
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  useEffect(() => {

    if (userData) {
      setFormData({
        dob: userData?.profileData?.dob || "",
        nationality: userData?.profileData?.nationality || "",
        preferredLanguage: userData?.profileData?.preferredLanguage || "",
        educationLevel: userData?.profileData?.educationLevel || "",
        employed: userData?.profileData?.employed || true,
        employmentType: userData?.profileData?.employmentType || "",
        residenceCountry: userData?.profileData?.residenceCountry || "",
        region: userData?.profileData?.region || "",
        city: userData?.profileData?.city || "",
        streetName: userData?.profileData?.streetName || "",
        grossIncome: userData?.profileData?.grossIncome || "",
        netWorth: userData?.profileData?.netWorth || "",
        sourceOfFunding: userData?.profileData?.sourceOfFunding || "",
        tradingObjectives: userData?.profileData?.tradingObjectives || "",
      });
    }
  }, [userData]);

  useEffect(() => {
    dispatch(getUserdata());
  }, [activeTab])
  const handleChange = (field) => (e) => {
    let val = e.target.value;

    if (field === "dob") {
      let digits = val.replace(/\D/g, ""); // keep only digits


      let day = digits.slice(0, 2);
      if (day) {
        const dayNum = parseInt(day, 10);
        if (dayNum > 31) day = "31";
      }


      let month = digits.slice(2, 4);
      if (month) {
        const monthNum = parseInt(month, 10);
        if (monthNum > 12) month = "12";
      }

      let year = digits.slice(4, 8);
      let formatted = "";
      if (digits.length <= 2) {
        formatted = day;
      } else if (digits.length <= 4) {
        formatted = day + "/" + month;
      } else {
        formatted = day + "/" + month + "/" + year;
      }


      if (digits.length === 2 && val[val.length - 1] !== "/") {
        formatted = day + "/";
      }

      if (digits.length === 4 && val[val.length - 1] !== "/") {
        formatted = day + "/" + month + "/";
      }

      val = formatted;
    }

    setFormData((prev) => ({ ...prev, [field]: val }));

    if (field === "residenceCountry") {
      if (val) {
        const filtered = countries.filter((c) =>
          c.toLowerCase().includes(val.toLowerCase())
        );
        setCountrySuggestions(filtered);
        setIsCountryDropdownOpen(true);
      } else {
        setCountrySuggestions([]);
        setIsCountryDropdownOpen(false);
      }
    }
  };

  // When user selects a country
  const handleSelectCountry = (country, hide = false) => {
    if (country) setFormData((prev) => ({ ...prev, residenceCountry: country }));
    setCountrySuggestions([]);
    setIsCountryDropdownOpen(false);
  };

  const handleReset = () => {
    if (userData) {
      setFormData({
        dob: userData?.dob || "",
        nationality: userData?.nationality || "",
        preferredLanguage: userData?.preferredLanguage || "",
        educationLevel: userData?.educationLevel || "",
        employed: userData?.employed || false,
        employmentType: userData?.employmentType || "",
        residenceCountry: userData?.residenceCountry || "",
        region: userData?.region || "",
        city: userData?.city || "",
        streetName: userData?.streetName || "",
        grossIncome: userData?.grossIncome || "",
        netWorth: userData?.netWorth || "",
        sourceOfFunding: userData?.sourceOfFunding || "",
        tradingObjectives: userData?.tradingObjectives || "",
      });
      setCountrySuggestions([]);
      setIsCountryDropdownOpen(false);
    }
  };

  const handleSave = () => {
    console.log("Save formData to backend:", formData);

    dispatch(
      sendProfiledata({
        userId: userData._id,
        payload: formData,
        callback: (data) => {
          console.log("data", data);
        },
      })
    );

  };

  if (!userData) return <div className="p-6" style={{ color: theme.textPrimary }}>Loading profile...</div>;

  return (
    <div className="p-6 min-h-screen space-y-6" style={{ backgroundColor: theme.bgPrimary }}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ProfileCard titleFirst="My" titleSecond="Profile" icon={<FaUser />}>
          <div className="flex flex-col md:flex-row items-end gap-4">
            <img
              src={profile}
              alt="User avatar"
              className="w-44 h-52 object-cover border rounded-lg"
              style={{ borderColor: theme.borderPrimary }}
            />
            <div className="w-full space-y-3">
              <Field label="Name" value={userData?.name} />
              <Field label="Email" value={userData?.email} />
              <Field label="Mobile" value={userData?.mobile} />
            </div>
          </div>
        </ProfileCard>

        <ProfileCard
          titleFirst="Personal"
          titleSecond="Information"
          icon={<FaInfoCircle />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date of Birth" value={formData.dob} onChange={handleChange("dob")} />
            <Field label="Primary Nationality" value={formData.nationality} onChange={handleChange("nationality")} />
            <Field label="Preferred Language" value={formData.preferredLanguage} onChange={handleChange("preferredLanguage")} />
            <Field label="Education Level" value={formData.educationLevel} onChange={handleChange("educationLevel")} />
            <Field
              label="Employed"
              value={formData.employed}
              onChange={handleChange("employed")}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
            />
            <Field label="Employment Type" value={formData.employmentType} onChange={handleChange("employmentType")} />
          </div>
        </ProfileCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <ProfileCard titleFirst="Personal" titleSecond="Address" icon={<FaFileAlt />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" ref={countryRef}>
            <Field
              label="Country Of Residence"
              value={formData.residenceCountry}
              onChange={handleChange("residenceCountry")}
              suggestions={isCountryDropdownOpen ? countrySuggestions : []}
              onSelectSuggestion={handleSelectCountry}
            />
            <Field label="State/Region" value={formData.region} onChange={handleChange("region")} />
            <Field label="City" value={formData.city} onChange={handleChange("city")} />
            <Field label="Street Name" value={formData.streetName} onChange={handleChange("streetName")} />
          </div>
        </ProfileCard>

        <ProfileCard titleFirst="Financial" titleSecond="Background" icon={<FaWallet />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Gross Annual Income (USD)" value={formData.grossIncome} onChange={handleChange("grossIncome")} />
            <Field label="Total Net Worth (USD)" value={formData.netWorth} onChange={handleChange("netWorth")} />
            <Field label="Source of Funding" value={formData.sourceOfFunding} onChange={handleChange("sourceOfFunding")} />
            <Field label="Trading Objectives" value={formData.tradingObjectives} onChange={handleChange("tradingObjectives")} />
          </div>
        </ProfileCard>
      </div>

      <div className="flex gap-4 justify-end mt-4">
        <button 
          className="px-6 py-2 rounded font-medium transition-all duration-200 hover:-translate-y-0.5" 
          onClick={handleReset}
          style={{ 
            backgroundColor: theme.buttonSecondary, 
            color: theme.buttonTextSecondary,
            boxShadow: `0 2px 8px ${theme.buttonSecondary}40`
          }}
        >
          Reset
        </button>
        <button 
          className="px-6 py-2 rounded font-medium transition-all duration-200 hover:-translate-y-0.5" 
          onClick={handleSave}
          style={{ 
            backgroundColor: theme.buttonPrimary, 
            color: theme.buttonTextPrimary,
            boxShadow: `0 2px 8px ${theme.buttonPrimary}40`
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default UserProfile;
